import { Button, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { MAP_CONFIG } from "./map-location.constants";
import { useMapLocation } from "./map-location.hook";
import {
  CoordinatesInfo,
  LoadingBox,
  MapContainer,
  MapElement,
  MapModeControls,
} from "./map-location.styles";

interface MapLocationModeLabels {
  read: string;
  edit: string;
  enableEdit: string;
  finishEdit: string;
}

interface MapLocationComponentProps {
  latitude: number;
  longitude: number;
  onCoordinatesChange: (lat: number, lng: number) => void;
  height?: number;
  allowModeToggle?: boolean;
  defaultMode?: "read" | "edit";
  modeLabels?: Partial<MapLocationModeLabels>;
}

export const MapLocationComponent: React.FC<MapLocationComponentProps> = ({
  latitude,
  longitude,
  onCoordinatesChange,
  height = 450,
  allowModeToggle = false,
  defaultMode = "edit",
  modeLabels,
}) => {
  const [interactionMode, setInteractionMode] = useState<"read" | "edit">(defaultMode);

  useEffect(() => {
    setInteractionMode(defaultMode);
  }, [defaultMode]);

  const labels = useMemo<MapLocationModeLabels>(
    () => ({
      read: "Read mode",
      edit: "Edit mode",
      enableEdit: "Edit location",
      finishEdit: "Finish editing",
      ...modeLabels,
    }),
    [modeLabels]
  );

  const { mapRef, currentLat, currentLng, isLoading, error, t } = useMapLocation({
    latitude,
    longitude,
    interactionMode,
    onCoordinatesChange,
  });

  const isEditing = interactionMode === "edit";

  return (
    <MapContainer height={height}>
      <MapElement ref={mapRef} />

      {isLoading && !error && (
        <LoadingBox>
          <CircularProgress size={40} />
          <Typography variant="body2" color="textSecondary">
            {t("loading_map")}
          </Typography>
        </LoadingBox>
      )}

      {error && (
        <LoadingBox>
          <Typography variant="body2" color="error" textAlign="center">
            {error}
          </Typography>
        </LoadingBox>
      )}

      {!isLoading && !error && (
        <>
          <MapModeControls>
            <Typography variant="caption" component="div" fontWeight={700}>
              {isEditing ? labels.edit : labels.read}
            </Typography>
            {allowModeToggle && (
              <Button
                variant="contained"
                size="small"
                onClick={() => setInteractionMode((prev) => (prev === "edit" ? "read" : "edit"))}
                sx={{
                  minWidth: 220,
                  fontWeight: 700,
                  color: "common.white",
                  backgroundColor: isEditing ? "primary.main" : "grey.900",
                  "&:hover": {
                    backgroundColor: isEditing ? "primary.dark" : "grey.800",
                  },
                }}
              >
                {isEditing ? labels.finishEdit : labels.enableEdit}
              </Button>
            )}
          </MapModeControls>

          <CoordinatesInfo>
            <Typography variant="caption" component="div" fontWeight={600}>
              {t("latitude")}: {currentLat.toFixed(MAP_CONFIG.COORDINATE_PRECISION)}
            </Typography>
            <Typography variant="caption" component="div" fontWeight={600}>
              {t("longitude")}: {currentLng.toFixed(MAP_CONFIG.COORDINATE_PRECISION)}
            </Typography>
          </CoordinatesInfo>
        </>
      )}
    </MapContainer>
  );
};
